import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faTriangleExclamation,
  faReceipt,
  faCircleCheck,
  faCircleHalfStroke,
  faCircleXmark,
  faGift,
  faScaleUnbalanced,
} from '@fortawesome/free-solid-svg-icons';
import './MyEarnings.css';

const API = process.env.REACT_APP_API_BASE_URL;

const TABS = [
  ['summary', 'Summary'],
  ['paid', 'Paid sessions'],
  ['demo', 'Demo sessions'],
  ['incentive', 'Incentives'],
  ['penalties', 'Penalties'],
];

// Same pay codes as before
const PAY = {
  0: { label: 'Full pay', tone: 'full', icon: faCircleCheck, factor: 1 },
  1: { label: 'Half pay', tone: 'half', icon: faCircleHalfStroke, factor: 0.5 },
  2: { label: 'No pay', tone: 'none', icon: faCircleXmark, factor: 0 },
};
const payOf = (v) => PAY[v] || { label: 'Unknown', tone: 'none', icon: faCircleXmark, factor: 0 };

const money = (n) => (Number(n) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const monthLabel = (y, m) => new Date(y, m, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
const when = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { day: '—', time: '' };
  return {
    day: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
    time: d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
  };
};

/* Same date the old page sent (today's day in the chosen month), but built
   from the LOCAL date — toISOString() used UTC and could slip to the wrong day/month. */
const requestDate = (y, m) => {
  const lastDay = new Date(y, m + 1, 0).getDate();
  const day = Math.min(new Date().getDate(), lastDay);
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

const MyEarnings = ({ userData }) => {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(([k]) => k === params.get('tab')) ? params.get('tab') : 'summary';

  const now = new Date();
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);

  const teacherId = userData?.id;
  const isCurrentMonth = ym.y === now.getFullYear() && ym.m === now.getMonth();

  const setTab = (key) => {
    const next = new URLSearchParams(params);
    if (key === 'summary') next.delete('tab');
    else next.set('tab', key);
    setParams(next, { replace: true });
  };

  const shiftMonth = (delta) => {
    setYm(({ y, m }) => {
      const d = new Date(y, m + delta, 1);
      const today = new Date();
      if (d.getFullYear() * 12 + d.getMonth() > today.getFullYear() * 12 + today.getMonth()) return { y, m }; // no future months
      return { y: d.getFullYear(), m: d.getMonth() };
    });
  };

  // ---------- Load (same endpoint) ----------
  useEffect(() => {
    if (!teacherId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const res = await axios.get(`${API}/api/teacher/earnings/${teacherId}/${requestDate(ym.y, ym.m)}`);
        if (!cancelled) setData(res.data || {});
      } catch (err) {
        console.error('Error fetching earnings:', err);
        if (!cancelled) {
          setData(null);
          setError('We couldn’t load your earnings for this month.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [teacherId, ym, reload]);

  // ---------- Derived lists ----------
  const paid = data?.paidDetails || [];
  const demos = data?.demoDetails || [];
  const extras = data?.incentiveData || [];
  const incentives = extras.filter((i) => i.Amount > 0);
  const penalties = extras.filter((i) => i.Amount < 0);
  const incentiveTotal = incentives.reduce((t, i) => t + i.Amount, 0);
  const penaltyTotal = penalties.reduce((t, i) => t + i.Amount, 0);
  // demo rate: from the server totals when possible (old page always showed $1.00)
  const demoRate = data?.demoCount ? (Number(data.demoPayTotal) || 0) / data.demoCount : 1;

  const counts = { paid: paid.length, demo: demos.length, incentive: incentives.length, penalties: penalties.length };

  /* ---------------- Tab content ---------------- */
  const renderSummary = () => {
    const rows = [
      { label: 'Demo sessions', count: data.demoCount || 0, amount: data.demoPayTotal },
      { label: 'Paid classes · full pay', count: data.fullPayCount || 0, amount: data.fullPayTotal },
      { label: 'Paid classes · half pay', count: data.halfPayCount || 0, amount: data.halfPayTotal },
      { label: 'Incentives', amount: data.incentive },
      { label: 'Arrears', amount: data.arrears },
      { label: 'Penalties', amount: data.penalties, negative: true },
    ];
    return (
      <ul className="rmtea-list rmtea-list--summary">
        {rows.map((r) => (
          <li key={r.label} className={`rmtea-row ${r.negative ? 'is-negative' : ''}`}>
            <span className="rmtea-row-main">{r.label}</span>
            <span className="rmtea-row-count">{r.count != null ? `× ${r.count}` : ''}</span>
            <span className="rmtea-amount">{money(r.amount)}</span>
          </li>
        ))}
        <li className="rmtea-row is-total">
          <span className="rmtea-row-main">Total earnings</span>
          <span className="rmtea-row-count" />
          <span className="rmtea-amount">{money(data.totalPay)}</span>
        </li>
      </ul>
    );
  };

  const renderSessions = (list, kind) => {
    if (!list.length) {
      return (
        <div className="rmtea-empty">
          <FontAwesomeIcon icon={faReceipt} />
          <p>{kind === 'paid' ? 'No paid classes recorded this month.' : 'No demo sessions this month.'}</p>
        </div>
      );
    }
    return (
      <ul className="rmtea-list">
        <li className="rmtea-row rmtea-row--head" aria-hidden="true">
          <span>Student</span>
          <span>Date</span>
          <span>Rate</span>
          <span>Status</span>
          <span className="rmtea-amount">Earned</span>
        </li>
        {list.map((d, i) => {
          const w = when(d.ClassDateTime);
          const pay = kind === 'paid' ? payOf(d.fullpay) : { label: 'Demo completed', tone: 'full', icon: faCircleCheck };
          const rate = kind === 'paid' ? Number(d.SessionRate) || 0 : demoRate;
          const earned = kind === 'paid' ? rate * pay.factor : demoRate;
          return (
            <li key={`${d.ClassDateTime}-${i}`} className="rmtea-row rmtea-row--session">
              <span className="rmtea-student">{d.fullname || 'Student'}</span>
              <span className="rmtea-when">
                {w.day}
                {w.time && <small>{w.time}</small>}
              </span>
              <span className="rmtea-rate">
                <small>Rate</small> {money(rate)}
              </span>
              <span className={`rmtea-pill is-${pay.tone}`}>
                <FontAwesomeIcon icon={pay.icon} /> {pay.label}
              </span>
              <span className="rmtea-amount">{money(earned)}</span>
            </li>
          );
        })}
      </ul>
    );
  };

  const renderExtras = (list, kind) => {
    const total = kind === 'incentive' ? incentiveTotal : penaltyTotal;
    if (!list.length) {
      return (
        <div className="rmtea-empty">
          <FontAwesomeIcon icon={kind === 'incentive' ? faGift : faCircleCheck} className={kind === 'penalties' ? 'is-good' : ''} />
          <p>{kind === 'incentive' ? 'No incentives this month.' : 'No penalties this month. Keep it up!'}</p>
        </div>
      );
    }
    return (
      <ul className="rmtea-list rmtea-list--summary">
        {list.map((item) => (
          <li key={item.Id} className={`rmtea-row ${kind === 'penalties' ? 'is-negative' : 'is-positive'}`}>
            <span className="rmtea-row-main">
              <FontAwesomeIcon icon={kind === 'incentive' ? faGift : faScaleUnbalanced} className="rmtea-row-icon" />
              {item.Title || (kind === 'incentive' ? 'Incentive pay' : 'Penalty')}
            </span>
            <span className="rmtea-row-count" />
            <span className="rmtea-amount">{money(item.Amount)}</span>
          </li>
        ))}
        <li className={`rmtea-row is-total ${kind === 'penalties' ? 'is-negative' : ''}`}>
          <span className="rmtea-row-main">Total</span>
          <span className="rmtea-row-count" />
          <span className="rmtea-amount">{money(total)}</span>
        </li>
      </ul>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="rmtea-list rmtea-list--loading" aria-busy="true">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="rmtea-skel" />
          ))}
        </div>
      );
    }
    if (error || !data) {
      return (
        <div className="rmtea-empty">
          <FontAwesomeIcon icon={faTriangleExclamation} className="is-error" />
          <p>{error || 'No data for this month.'}</p>
          <button type="button" className="rmtea-btn" onClick={() => setReload((n) => n + 1)}>
            Try again
          </button>
        </div>
      );
    }
    if (tab === 'paid') return renderSessions(paid, 'paid');
    if (tab === 'demo') return renderSessions(demos, 'demo');
    if (tab === 'incentive') return renderExtras(incentives, 'incentive');
    if (tab === 'penalties') return renderExtras(penalties, 'penalties');
    return renderSummary();
  };

  return (
    <div className="rmtea">
      <div className="rmtea-inner">
        {/* ---------- Header + month ---------- */}
        <header className="rmtea-head">
          <div>
            <span className="rmtea-eyebrow">My Earnings</span>
            <h1 className="rmtea-title">
              Your <span>earnings</span>
            </h1>
          </div>
          <div className="rmtea-month">
            <button type="button" onClick={() => shiftMonth(-1)} aria-label="Previous month">
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <strong>{monthLabel(ym.y, ym.m)}</strong>
            <button type="button" onClick={() => shiftMonth(1)} disabled={isCurrentMonth} aria-label="Next month">
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </header>

        {/* ---------- Total card ---------- */}
        <section className="rmtea-hero" aria-label="Month total">
          <div className="rmtea-total">
            <span>{isCurrentMonth ? 'Earned so far this month' : `Total for ${monthLabel(ym.y, ym.m)}`}</span>
            <strong>{loading || !data ? '—' : money(data.totalPay)}</strong>
          </div>
          <ul className="rmtea-chips">
            <li>
              <b>{loading || !data ? '–' : (data.fullPayCount || 0) + (data.halfPayCount || 0)}</b> paid classes
            </li>
            <li>
              <b>{loading || !data ? '–' : data.demoCount || 0}</b> demos
            </li>
            <li className="is-positive">
              <b>{loading || !data ? '–' : money(data.incentive)}</b> incentives
            </li>
            <li className="is-negative">
              <b>{loading || !data ? '–' : money(data.penalties)}</b> penalties
            </li>
          </ul>
        </section>

        {/* ---------- Tabs ---------- */}
        <div className="rmtea-tabs" role="tablist" aria-label="Earnings sections">
          {TABS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              className={`rmtea-tab ${tab === key ? 'is-on' : ''}`}
              onClick={() => setTab(key)}
            >
              {label}
              {!loading && data && counts[key] > 0 && <span>{counts[key]}</span>}
            </button>
          ))}
        </div>

        <div className="rmtea-panel" role="tabpanel">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default MyEarnings;